/**
 * HTTP response envelopes.
 *
 * These mirror the route handlers in `apps/api/src/routes/` exactly. They are
 * hand-written rather than inferred from the Worker's source because the Worker
 * is a separate compilation unit - it is typechecked against
 * `@cloudflare/workers-types`, which the browser tsconfig does not include, so
 * importing a route handler's type into the client would drag workerd globals
 * into a DOM program.
 *
 * That is a real, deliberate gap: nothing here is machine-checked against the
 * server. The mitigation is that every payload that matters on the wire - cards,
 * columns, comments, events, presence - is typed by `@slate/shared`, which both
 * sides do import. Only the thin envelope around them is duplicated, and an
 * envelope mismatch fails loudly on the first request rather than subtly.
 */

import type { z } from 'zod';
import {
  updateCardBodySchema,
  type Board,
  type BoardEvent,
  type BoardSnapshot,
  type Card,
  type CardAttachment,
  type CardComment,
  type Column,
  type Role,
  type User,
  type WorkspaceMember,
  type WorkspaceWithMembership,
} from '@slate/shared';

/**
 * The body of `PATCH /api/boards/:boardId/cards/:cardId`.
 *
 * Derived from the shared Zod schema rather than restated, so the client's
 * optimistic patch and the server's validator cannot drift. `z.infer` is used
 * rather than a hand-written interface because the schema is the artefact both
 * sides already agree on - the same object validates the request on the Worker
 * and types the call here.
 */
export type CardPatchBody = z.infer<typeof updateCardBodySchema>;

export interface SessionResponse {
  user: User;
  accessToken: string;
  /** ISO-8601. The client schedules a proactive refresh a minute before this,
   * rather than waiting for a 401 and paying a round trip on a user action. */
  expiresAt: string;
}

export interface MeResponse {
  user: User;
}

export interface WorkspaceListResponse {
  workspaces: WorkspaceWithMembership[];
}

export interface WorkspaceDetailResponse {
  workspace: WorkspaceWithMembership;
  members: WorkspaceMember[];
}

export interface BoardListResponse {
  boards: Board[];
}

export interface BoardResponse {
  board: Board | null;
}

/** `GET /api/boards/:id/state` without `since`. */
export interface SnapshotEnvelope extends BoardSnapshot {
  mode: 'snapshot';
}

/** `GET /api/boards/:id/state?since=` when the gap is small enough to replay. */
export interface DeltaEnvelope {
  mode: 'delta';
  board: { id: string };
  sequence: number;
  events: BoardEvent[];
}

/**
 * `GET /api/boards/:id/state?since=` when it is not.
 *
 * The server makes this call rather than the client, because only the server
 * knows how many events the gap contains. See `loadBoardDelta` in
 * `apps/api/src/lib/snapshot.ts`.
 */
export interface ResyncEnvelope {
  resyncRequired: true;
  sequence: number;
}

export type BoardStateResponse = SnapshotEnvelope | DeltaEnvelope | ResyncEnvelope;

export function isSnapshot(response: BoardStateResponse): response is SnapshotEnvelope {
  return 'columns' in response && 'cards' in response;
}

export function isDelta(response: BoardStateResponse): response is DeltaEnvelope {
  return 'mode' in response && response.mode === 'delta';
}

export function isResync(response: BoardStateResponse): response is ResyncEnvelope {
  return 'resyncRequired' in response;
}

export interface ActivityResponse {
  events: BoardEvent[];
  /** `null` means the end of the feed. Returning a cursor that yields an empty
   * page instead would make every client implement fetch-until-empty. */
  nextCursor: string | null;
}

export interface PresenceResponse {
  presence: unknown[];
  sequence: number;
}

export interface CardWriteResponse {
  card: unknown;
  sequence: number | null;
  /** Present only on the move endpoint, and only when the move was a no-op. */
  noop?: boolean;
}

/** `GET /api/boards/:boardId/cards/:cardId` - the whole drawer in one response. */
export interface CardDetailResponse {
  card: Card;
  comments: CardComment[];
  attachments: CardAttachment[];
  history: BoardEvent[];
}

export interface CommentWriteResponse {
  comment: CardComment | null;
  sequence: number | null;
}

export interface AttachmentWriteResponse {
  attachment: CardAttachment | null;
  sequence: number | null;
}

export interface AttachmentListResponse {
  attachments: CardAttachment[];
  maxBytes: number;
  inlineCapBytes: number;
  driver: 'inline' | 'r2';
}

export interface ColumnWriteResponse {
  column: Column | null;
  sequence: number | null;
}

export interface MemberWriteResponse {
  member: WorkspaceMember | null;
}

/** The workspace role the current user holds, as returned by the membership
 * endpoints. Re-exported here so components do not import from `@slate/shared`
 * for one type and from this module for another. */
export type { Role };
